import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Card, Descriptions, Statistic, Tag, Typography,
} from 'antd';
import {
  daysBetween, formatDateOnly, formatDuration, formatDurationSeconds, formatTimestamp,
} from '../runUtils';

const { Text, Paragraph } = Typography;

const fmtRatio = (value, digits = 2) => (
  value == null || !Number.isFinite(Number(value)) ? null : Number(value).toFixed(digits)
);

/**
 * One-line description of the pipeline's forecast calibration, e.g.
 * "×1.18 (model 0.86 → anchor 1.01 YoY, remaining FY share 0.64)". Null when absent.
 */
const describeCalibration = (calibration) => {
  if (!calibration || typeof calibration !== 'object') return null;
  const factor = fmtRatio(calibration.calibrationFactor);
  const model = fmtRatio(calibration.rawModelRatio);
  const anchor = fmtRatio(calibration.yoyAnchor);
  const share = fmtRatio(calibration.remainingFyShare);
  if (!factor && !model && !anchor && !share) return null;
  const details = [];
  if (model && anchor) details.push(`model ${model} → anchor ${anchor} YoY`);
  else if (anchor) details.push(`anchor ${anchor} YoY`);
  if (share) details.push(`remaining FY share ${share}`);
  const head = factor ? `×${factor}` : 'n/a';
  return details.length ? `${head} (${details.join(', ')})` : head;
};

const DataProcessedCard = ({ run }) => {
  const summary = run?.summaryJson;
  const scoring = summary?.scoring;
  const hasScoring = scoring && typeof scoring === 'object';
  const calibrationText = hasScoring ? describeCalibration(scoring.forecastCalibration) : null;
  const testMode = !!(summary?.testMode || scoring?.testMode);
  const lagDays = hasScoring
    ? daysBetween(scoring.salesDataThrough, scoring.maxInvoiceDateLoaded)
    : null;
  const duration = formatDuration(run?.scoringStartedAt, run?.scoringFinishedAt)
    || formatDurationSeconds(scoring?.runtimeSeconds);

  return (
    <Card
      className="segmentation-section"
      title="Data processed"
      extra={testMode ? <Tag color="red">Test mode</Tag> : null}
    >
      {!hasScoring && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Sales-as-of metadata unavailable for this run"
          description="Older runs (or runs scored before the pipeline metadata deploy) only show scoring timestamps and the BigQuery results table. Trigger a new run after deploying the updated scoring services to see sales data through / max invoice."
        />
      )}

      {hasScoring && (
        <div className="segmentation-stat-grid" style={{ marginBottom: 16 }}>
          <div className="segmentation-stat">
            <Statistic
              title="Sales data through"
              value={formatDateOnly(scoring.salesDataThrough)}
            />
          </div>
          <div className="segmentation-stat">
            <Statistic
              title="Max invoice loaded"
              value={formatDateOnly(scoring.maxInvoiceDateLoaded)}
            />
            {lagDays != null && lagDays > 0 && (
              <Text type="warning" style={{ fontSize: 12 }}>
                {`${lagDays} day lag vs cutoff`}
              </Text>
            )}
          </div>
          <div className="segmentation-stat">
            <Statistic
              title="Customers scored"
              value={scoring.customerCount ?? summary?.totalCustomers ?? '—'}
            />
          </div>
          <div className="segmentation-stat">
            <Statistic
              title="Sales rows loaded"
              value={scoring.salesRowCount ?? '—'}
            />
          </div>
        </div>
      )}

      <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered>
        <Descriptions.Item label="Reference date">
          {hasScoring
            ? `${formatDateOnly(scoring.referenceDate)}${scoring.referenceTimezone ? ` (${scoring.referenceTimezone})` : ''}`
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Financial year">
          {scoring?.financialYear ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Scoring started">
          {formatTimestamp(run?.scoringStartedAt)}
        </Descriptions.Item>
        <Descriptions.Item label="Scoring finished">
          {formatTimestamp(run?.scoringFinishedAt)}
          {duration ? ` (${duration})` : ''}
        </Descriptions.Item>
        <Descriptions.Item label="Source sales table" span={2}>
          <Text code copyable={!!scoring?.sourceSalesTable}>
            {scoring?.sourceSalesTable ?? '—'}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="Source last modified">
          {formatTimestamp(scoring?.sourceSalesLastModified)}
        </Descriptions.Item>
        <Descriptions.Item label="Source table rows">
          {scoring?.sourceSalesRowCount ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Results table" span={2}>
          <Text code copyable={!!run?.bqTable}>
            {run?.bqTable ?? scoring?.resultsTable ?? '—'}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="Triggered by">
          {run?.triggeredByEmail ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Triggered via">
          {run?.triggeredVia ?? '—'}
        </Descriptions.Item>
        {calibrationText && (
          <Descriptions.Item label="Forecast calibration" span={2}>
            {calibrationText}
          </Descriptions.Item>
        )}
      </Descriptions>

      {hasScoring && (
        <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
          Cutoff is invoices strictly before the 1st of the month in the region timezone
          — sales data through is the last day of the prior month. Max invoice loaded shows
          the newest invoice actually present in the source extract.
        </Paragraph>
      )}
    </Card>
  );
};

DataProcessedCard.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  run: PropTypes.object,
};

DataProcessedCard.defaultProps = {
  run: null,
};

export default DataProcessedCard;
